import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/AuthV2`;

// ============================================================
// Servicio de Gestión de Sesiones
// Controller: AuthV2Controller (logout, validate-session)
// Ruta base: /api/AuthV2
// Autenticación: JWT Bearer (todos los endpoints)
// Nota: sessionId y employeeId se extraen del JWT automáticamente en el backend
//
// Políticas de sesión HIPAA:
// - Sesión única por usuario (1 sesión activa)
// - Timeout de inactividad: 30 minutos
// - Expiración absoluta: 8 horas (un turno laboral)
// - JWT expira en 8 horas (alineado con la sesión)
// ============================================================

/**
 * Cierre voluntario de sesión.
 * No requiere body. El sessionId y employeeId se extraen del JWT automáticamente.
 * Es idempotente: si la sesión ya está cerrada, retorna éxito ("Session is already closed").
 *
 * Después de recibir la respuesta, el frontend DEBE:
 * 1. Eliminar el JWT de localStorage
 * 2. Redirigir a la pantalla de login
 * (Incluso si el backend retorna error, eliminar JWT de todos modos)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Logout successful" }
 * -- o si ya estaba cerrada:
 * { "result": 0, "message": "Session is already closed" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Session not found for this user" }
 */
export const logout = async () => {
  try {
    const response = await axios.post(
      `${API_URL}/logout`,
      null,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en logout:", error);
    throw error;
  }
};

/**
 * Validar que la sesión del usuario sigue activa.
 * Útil para:
 * - Verificar sesión al cargar la app
 * - Polling periódico (cada 5 min) para detectar cierre forzado por admin
 * - Detectar timeout por inactividad (30 min sin actividad)
 *
 * Cada llamada actualiza DAT_LAST_ACTIVITY en BD (si ha pasado >1 minuto).
 * El sessionId se extrae del JWT automáticamente.
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Session is valid",
 *   "employeeId": 1,                           // ID del empleado
 *   "expiry": "2026-02-10T09:47:58",           // Expiración absoluta de la sesión
 *   "lastActivity": "2026-02-10T02:15:30"      // Última actividad registrada
 * }
 *
 * Posibles errores (401):
 * { "result": 1, "message": "Session not found" }
 * { "result": 2, "message": "Session is closed or inactive" }
 * { "result": 3, "message": "Session expired due to inactivity timeout" }
 *
 * Si retorna error (1, 2 o 3), el frontend debe:
 * 1. Limpiar JWT de localStorage
 * 2. Redirigir a login (opcionalmente con reason=session_expired en query params)
 */
export const validateSession = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/validate-session`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    // 401 es esperado cuando la sesión es inválida
    if (error.response && error.response.status === 401) {
      return error.response.data;
    }
    console.error("Error en validateSession:", error);
    throw error;
  }
};
